import type { EventStatus, Permission } from '@barso/shared';
import {
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';
import { NavLink, Outlet, matchPath, useLocation, useParams } from 'react-router';
import { bazaarApi, type BazaarEvent, type EventSummary } from '../api/endpoints';
import { loadOrganizations } from '../hooks/useOrganizations';
import { Icon } from './icons';
import { setPosEventTitle } from '../pos/sessions';
import {
  AREAS,
  AREA_GROUPS,
  featuresOfEvent,
  visibleAreas,
  type Area,
  type AreaFeatures,
} from './areas';
import { navContextStore } from './navContext';
import { CASH_PAGE } from './workPages';

/**
 * Höhe der Leiste als CSS-Variable `--event-bar-h` (für die Suchleisten darunter, die sich unter ihr festsetzen).
 * Ohne `ResizeObserver` (alte Browser, Tests) bleibt der Standardwert im Stylesheet.
 */
function useBarHeight(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const node = ref.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const apply = () => {
      document.documentElement.style.setProperty('--event-bar-h', `${String(node.offsetHeight)}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--event-bar-h');
    };
  }, [ref]);
}

/**
 * Ab hier passt die Leiste mit allen Gruppen in eine Zeile. Darunter (Handy, Tablet hochkant) übernimmt die untere
 * Leiste mit dem Blatt „Mehr“ alle Bereiche, eine Navigation statt zweier.
 */
const NARROW_QUERY = '(max-width: 900px)';

/** Schmaler Bildschirm (Handy, Tablet hochkant): keine Basar-Leiste, siehe `NARROW_QUERY`. Ohne `matchMedia` (Tests): breit. */
function useNarrow(): boolean {
  return useSyncExternalStore(
    (notify) => {
      if (typeof window.matchMedia !== 'function') return () => undefined;
      const query = window.matchMedia(NARROW_QUERY);
      query.addEventListener('change', notify);
      return () => {
        query.removeEventListener('change', notify);
      };
    },
    () => typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches,
    () => false,
  );
}

/** Bereiche, die die Person sehen darf (rein, damit die Rechte-Abbildung testbar bleibt). */
export function eventNavEntries(
  permissions: Permission[],
  features?: AreaFeatures,
  status?: EventStatus,
): Area[] {
  return visibleAreas(permissions, features, status);
}

/** Gruppen mit ihren sichtbaren Bereichen (leere Gruppen entfallen). */
export function eventNavGroups(
  permissions: Permission[],
  features?: AreaFeatures,
  status?: EventStatus,
): { key: string; title: string; areas: Area[] }[] {
  const areas = visibleAreas(permissions, features, status);
  return AREA_GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    areas: areas.filter((area) => area.group === group.key),
  })).filter((group) => group.areas.length > 0);
}

/** Der Bereich, zu dem die Adresse gehört (Übersicht nur bei genauer Adresse). */
function activeArea(base: string, pathname: string, areas: Area[]): Area | null {
  return (
    areas.find((area) =>
      area.path === ''
        ? matchPath({ path: base, end: true }, pathname) !== null
        : matchPath({ path: `${base}${area.path}`, end: false }, pathname) !== null,
    ) ?? null
  );
}

function AreaLink({ base, area, onClick }: { base: string; area: Area; onClick?: () => void }) {
  return (
    <NavLink to={`${base}${area.path}`} end={area.path === ''} onClick={onClick}>
      <Icon name={area.icon} size={18} />
      {area.title}
    </NavLink>
  );
}

/** Hervorgehobener Einstieg „Kasse öffnen“ (oben im Menü Basartag und im Menü-Blatt am Handy). */
function FeaturedLink({ base, area, onClick }: { base: string; area: Area; onClick?: () => void }) {
  return (
    <NavLink to={`${base}${area.path}`} className="event-nav__featured" onClick={onClick}>
      <Icon name={area.icon} size={18} />
      {area.title} öffnen
    </NavLink>
  );
}

/**
 * Aufklapp-Menü einer Gruppe (Disclosure-Muster): Knopf mit `aria-expanded`/`aria-controls`, darunter die Links.
 * Pfeiltasten wechseln zwischen den Einträgen, Escape schließt und gibt den Fokus an den Knopf zurück, ein Klick
 * oder Fokus außerhalb schließt ebenfalls.
 */
function GroupMenu({
  base,
  title,
  areas,
  featured,
  active,
  open,
  onOpenChange,
}: {
  base: string;
  title: string;
  areas: Area[];
  /** Hervorgehobener Einstieg oben im Menü („Kasse öffnen“). */
  featured: Area | null;
  active: Area | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const id = useId();
  const root = useRef<HTMLLIElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const isActive =
    active !== null &&
    (active.key === featured?.key || areas.some((area) => area.key === active.key));
  const focusItem = (index: number) => {
    const links = list.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [];
    links[(index + links.length) % links.length]?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
        button.current?.focus();
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, onOpenChange]);

  const onButtonKey = (event: ReactKeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onOpenChange(true);
      // Die Liste entsteht erst nach dem Rendern
      window.setTimeout(() => {
        focusItem(0);
      }, 0);
    }
  };
  const onListKey = (event: ReactKeyboardEvent) => {
    const links = Array.from(list.current?.querySelectorAll<HTMLAnchorElement>('a') ?? []);
    const index = links.findIndex((link) => link === document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusItem(index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusItem(index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusItem(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusItem(links.length - 1);
    }
  };

  return (
    <li
      className="event-nav__group"
      ref={root}
      onBlur={(event) => {
        // Fokus verlässt die Gruppe (z. B. per Tab): schließen
        const next = event.relatedTarget;
        if (open && next instanceof Node && !event.currentTarget.contains(next))
          onOpenChange(false);
      }}
    >
      <button
        type="button"
        ref={button}
        className="event-nav__button"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        data-active={isActive ? 'true' : undefined}
        onClick={() => {
          onOpenChange(!open);
        }}
        onKeyDown={onButtonKey}
      >
        <span>{title}</span>
        {isActive && <span className="event-nav__current">{active.short}</span>}
        <Icon name="chevron-down" size={16} />
      </button>
      {open && (
        <ul className="event-nav__menu" id={id} ref={list} onKeyDown={onListKey}>
          {featured !== null && (
            <li>
              <FeaturedLink
                base={base}
                area={featured}
                onClick={() => {
                  onOpenChange(false);
                }}
              />
            </li>
          )}
          {areas.map((area) => (
            <li key={area.key}>
              <AreaLink
                base={base}
                area={area}
                onClick={() => {
                  onOpenChange(false);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Menüleiste eines Basars in einer Zeile: „Übersicht“, dann die Gruppen als Aufklapp-Menüs (Vorbereitung, Basartag,
 * Kommunikation, Abschluss) und ganz rechts „Mehr“ für Seltenes und Einstellungen. Sichtbar nur, was die eigenen Rechte
 * erlauben. Die Gruppe mit der aktiven Seite ist hervorgehoben und nennt sie. Am Handy steht diese Leiste nicht: dort
 * führt die untere Leiste mit dem Blatt „Mehr“ zu denselben Bereichen.
 */
export function EventNav({
  eventId,
  permissions,
  features,
  status,
}: {
  eventId: string;
  permissions: Permission[];
  /** Schalter der Zusatzfunktionen: Bereiche einer ausgeschalteten Funktion erscheinen nicht. */
  features?: AreaFeatures;
  /** Status des Basars: Bereiche, die nur in einem Status gelten (z. B. „Anmeldung teilen“). */
  status?: EventStatus;
}) {
  const base = `/basare/${eventId}`;
  const location = useLocation();
  const narrow = useNarrow();
  const areas = eventNavEntries(permissions, features, status);
  const kasse = areas.find((area) => area.key === AREAS.kasse.key) ?? null;
  const groups = eventNavGroups(permissions, features, status).map((group) =>
    group.key === 'basartag' && kasse !== null
      ? { ...group, areas: group.areas.filter((area) => area.key !== kasse.key) }
      : group,
  );
  const active = activeArea(base, location.pathname, areas);
  // Geöffnet gilt nur für die Seite, auf der es geöffnet wurde: ein Seitenwechsel schließt es
  const [openState, setOpenState] = useState<{ key: string; path: string } | null>(null);
  const openKey = openState?.path === location.pathname ? openState.key : null;
  const setOpen = (key: string) => (value: boolean) => {
    setOpenState((current) => {
      if (value) return { key, path: location.pathname };
      return current?.key === key ? null : current;
    });
  };
  const bar = useRef<HTMLElement>(null);
  useBarHeight(bar);
  const start = areas.find((area) => area.key === AREAS.start.key);

  if (narrow) return null;
  return (
    <nav className="event-nav" aria-label="Basar-Menü" ref={bar}>
      <ul className="event-nav__list">
        {start !== undefined && (
          <li>
            <AreaLink base={base} area={start} />
          </li>
        )}
        {groups.map((group) => (
          <GroupMenu
            key={group.key}
            base={base}
            title={group.title}
            areas={group.areas}
            featured={group.key === 'basartag' ? kasse : null}
            active={active}
            open={openKey === group.key}
            onOpenChange={setOpen(group.key)}
          />
        ))}
      </ul>
    </nav>
  );
}

/**
 * Hülle um alle Seiten unter `/basare/:eventId`: lädt den Basar (Titel, Rechte), zeigt die Menüleiste (am Handy als Menü-Knopf) und gibt Basar und Organisation an Kopfzeile und Brotkrumen weiter. Scheitert
 * das Laden (z. B. Kasse ohne Netz), bleibt die Seite selbst voll nutzbar, nur die Leiste fehlt.
 */
export function EventShell() {
  const { eventId = '' } = useParams();
  const location = useLocation();
  const [event, setEvent] = useState<BazaarEvent | EventSummary | null>(null);
  const [orgName, setOrgName] = useState<{ id: string; name: string } | null>(null);
  const loadedFor = useRef<string | null>(null);

  // Nur beim Wechsel des Basars (und wenn der Tab wieder sichtbar wird) neu laden, nicht bei jedem Seitenwechsel im Basar
  useEffect(() => {
    const guard = { cancelled: false };
    if (loadedFor.current !== eventId) setEvent(null);
    const load = () => {
      bazaarApi.getEvent(eventId).then(
        (loaded) => {
          if (guard.cancelled) return;
          loadedFor.current = eventId;
          setEvent(loaded);
        },
        () => undefined,
      );
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      guard.cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [eventId]);

  const organizationId = event?.organizationId ?? null;
  useEffect(() => {
    if (organizationId === null) return;
    const guard = { cancelled: false };
    loadOrganizations().then(
      (orgs) => {
        const found = orgs.find((org) => org.id === organizationId);
        if (!guard.cancelled && found !== undefined) {
          setOrgName({ id: organizationId, name: found.name });
        }
      },
      () => undefined,
    );
    return () => {
      guard.cancelled = true;
    };
  }, [organizationId]);

  const title = event?.title ?? null;
  const canSell = event?.myPermissions?.includes('event.sales.create') === true;
  const name = orgName !== null && orgName.id === organizationId ? orgName.name : null;
  // Schalter einzeln als Abhängigkeit (ein neues Objekt je Rendern würde den Effekt jedes Mal auslösen)
  const featureFlags = featuresOfEvent(event ?? {});
  const { runSheet, push, payoutTransfer, helperBonus } = featureFlags;
  useEffect(() => {
    if (title === null) return;
    setPosEventTitle(eventId, title);
    navContextStore.set({
      eventId,
      eventTitle: title,
      orgId: organizationId,
      orgName: name,
      canSell,
      status: event?.status,
      permissions: event?.myPermissions,
      features: { runSheet, push, payoutTransfer, helperBonus },
    });
  }, [
    eventId,
    title,
    organizationId,
    name,
    canSell,
    event?.status,
    event?.myPermissions,
    runSheet,
    push,
    payoutTransfer,
    helperBonus,
  ]);
  useEffect(
    () => () => {
      navContextStore.clear();
    },
    [],
  );

  const permissions = event === null ? null : (event.myPermissions ?? []);
  // Kassen-Modus: die Kasse hat ihre eigene Kopfzeile mit Menü, keine zweite Leiste darüber
  const cash = CASH_PAGE.test(location.pathname);
  if (cash || permissions === null) return <Outlet />;
  const features = event === null ? {} : featuresOfEvent(event);
  return (
    <div className="basar-shell">
      <div className="basar-shell__body">
        <EventNav
          eventId={eventId}
          permissions={permissions}
          features={features}
          status={event?.status}
        />
        <Outlet />
      </div>
    </div>
  );
}
